/**
 * ClientDetailPage Component (TASK-137)
 *
 * Client detail page with edit form and estimates list.
 * KAN-746: the deals block (estimates or orders) is ClientDealsCard, picked by
 * cabinet mode; its labels come from lib/modeLabels.
 *
 * TASK-342: DELETE now performs a FULL forget on the backend (estimates,
 * conversation history, call recordings, scheduled callbacks). The confirm
 * dialog warns honestly about that, and the delete button is gated behind
 * the `users.manage` permission - mirroring the backend 403 gate (same
 * currentUser-query pattern as ColdCallsView / TASK-338).
 *
 * KAN-686: the status block tells who answers the client (bot, manual mode or
 * "Передан менеджеру: бот отвечает, сам не пишет" with the "Вернуть боту" button).
 *
 * KAN-806: the "Telegram" field edits the username (not the numeric id); it
 * goes in the same PATCH as the common fields (hooks/useClientCardSave).
 */
import { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Save,
  Trash2,
  Loader2,
  AlertCircle,
  Phone,
  Mail,
  MessageCircle,
  Calendar,
  User,
  MessageSquare,
  TrendingUp,
} from 'lucide-react';
import { PageLayout } from '@/components/layout/PageLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ClientAvatar } from '@/components/clients/ClientAvatar';
import { ClientLinksCard } from '@/components/clients/ClientLinksCard';
import { ReferralQueueNotice } from '@/components/clients/ReferralQueueNotice';
import { BotSilenceStatus } from '@/components/clients/BotSilenceStatus';
import { OwnContactSwitch } from '@/components/clients/OwnContactSwitch';
import { ClientManagerSelect } from '@/components/clients/ClientManagerSelect';
import { ClientLeadTemperatureSelect } from '@/components/clients/ClientLeadTemperatureSelect';
import { ClientDealsCard } from '@/components/clients/ClientDealsCard';
import { NextBotTouchNote } from '@/components/clients/NextBotTouchNote';
import { ClientHeaderCall } from '@/components/clients/ClientHeaderCall';
import { ClientCompanyField } from '@/components/clients/ClientCompanyField';
import { ClientJunkControls } from '@/components/clients/ClientJunkButtons';
import { ClientReopenButton } from '@/components/clients/ClientReopenButton';
import { NextContactAction } from '@/components/clients/NextContactAction';
import { clientNextContactTarget } from '@/lib/nextContact';
import { useModeLabels } from '@/hooks/useModeLabels';
import {
  buildClientCardPayload,
  useClientCardSave,
} from '@/hooks/useClientCardSave';
import {
  normalizeTelegramUsername,
  telegramUsernameEdit,
  telegramUsernameInputValue,
} from '@/lib/crmRowEdit';
import { LeadContactsCard, TelegramIconLink } from '@/components/clients/TelegramContacts';
import { LeadAttributesCard } from '@/components/clients/LeadAttributesCard';
import { ClientProfileDataCard } from '@/components/clients/ClientProfileDataCard';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { clientsApi } from '@/services/api/clients';
import { funnelApi } from '@/services/api/funnel';
import { apiClient } from '@/services/api/client';
import { ContactCard } from '@/components/funnel/ContactCard';
import type {
  UpdateClientPayload,
  ClientFunnelPosition,
} from '@/types/client';
import type { FunnelContact, FunnelStageConfig } from '@/types/funnel';
import { useCanCallApi } from '@/hooks/useApiAccess';
import { useOrgTimezone } from '@/hooks/useOrgTimezone';
import { ANY_UUID } from '@/lib/managerAccess';

export function ClientDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // KAN-746: "сметы" / "заказы" by cabinet mode.
  const labels = useModeLabels();
  // KAN-840: запланированный созвон по времени организации.
  const orgTimeZone = useOrgTimezone();

  // Form state
  const [form, setForm] = useState<UpdateClientPayload>({});
  const [isDirty, setIsDirty] = useState(false);
  // KAN-806: the username lives apart from the common form: it is sent only
  // when it differs from the saved one.
  const [telegramUsername, setTelegramUsername] = useState('');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // Set page title
  useEffect(() => {
    document.title = 'Клиент - SMETA AI';
  }, []);

  // Query client data
  const {
    data: client,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['client', id],
    queryFn: () => clientsApi.getById(id!),
    enabled: !!id,
  });

  // KAN-811: touchpoints and deleting are closed to the MANAGER role.
  const canSeeContacts = useCanCallApi('GET', '/api/v1/contacts/');
  const canDelete = useCanCallApi('DELETE', `/api/v1/clients/${id ?? ANY_UUID}/`);
  // KAN-889: the decision-maker contact is edited by the card PATCH.
  const canEditCard = useCanCallApi('PATCH', `/api/v1/clients/${id ?? ANY_UUID}/`);

  // Query client contacts (touchpoints)
  const { data: contacts, isLoading: contactsLoading } = useQuery({
    queryKey: ['client-contacts', id],
    queryFn: () => funnelApi.getContacts(id!),
    enabled: !!id && canSeeContacts,
  });

  // Query funnel stages (for the funnel widget)
  const { data: stages, isLoading: stagesLoading } = useQuery({
    queryKey: ['funnel-stages'],
    queryFn: funnelApi.getStages,
    enabled: !!id,
  });

  // TASK-342 RBAC: only managers (users.manage) see the destructive delete -
  // the backend destroy is gated the same way (403 without the permission).
  // Same currentUser-query pattern as ColdCallsView (TASK-338).
  const { data: currentUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/auth/me/');
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });
  const canManage = currentUser?.permissions?.users?.manage === true && canDelete;

  // Initialize form when client data loads
  useEffect(() => {
    if (client) {
      setForm({
        phone: client.phone,
        name: client.name,
        email: client.email,
        notes: client.notes,
      });
    }
  }, [client]);

  // KAN-806: reset the field only when the SAVED username changes, so a
  // refetch with the same saved value keeps what the user typed.
  const savedUsername = client?.telegram_username;
  useEffect(() => {
    setTelegramUsername(telegramUsernameInputValue(savedUsername));
  }, [savedUsername]);
  const usernameEdit = telegramUsernameEdit(telegramUsername, savedUsername);
  const canSave = isDirty || usernameEdit !== null;
  // Link to the SAVED username, not to the draft.
  const savedTelegramName = normalizeTelegramUsername(savedUsername ?? '');
  const telegramHref = savedTelegramName
    ? `https://t.me/${savedTelegramName}`
    : null;
  const telegramLinked = !!client?.telegram_id;

  // Update mutation: one PATCH with the common fields and the username
  const updateMutation = useClientCardSave(id, {
    onSaved: () => setIsDirty(false),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => clientsApi.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      navigate('/clients');
    },
  });

  // Handlers
  const handleBack = useCallback(() => {
    navigate('/clients');
  }, [navigate]);

  const handleFormChange = useCallback(
    (field: keyof UpdateClientPayload, value: string | number | null) => {
      setForm((prev) => ({ ...prev, [field]: value }));
      setIsDirty(true);
    },
    []
  );

  const handleSave = useCallback(() => {
    updateMutation.mutate(
      buildClientCardPayload(isDirty ? form : null, usernameEdit)
    );
  }, [form, isDirty, usernameEdit, updateMutation]);

  const handleDelete = useCallback(() => {
    setIsDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    deleteMutation.mutate();
  }, [deleteMutation]);

  // Format date
  const formatDate = useCallback((dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <PageLayout
        data-testid="client-detail-page"
        titleIcon={
          <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
            <User className="h-5 w-5 text-white" />
          </div>
        }
        title="Загрузка..."
        breadcrumbs={[
          { label: 'Главная', href: '/' },
          { label: 'Контакты', href: '/clients' },
          { label: 'Загрузка...' },
        ]}
      >
        <div
          data-testid="client-loading"
          className="flex items-center justify-center py-12"
        >
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </PageLayout>
    );
  }

  // Error state
  if (isError || !client) {
    return (
      <PageLayout
        data-testid="client-detail-page"
        titleIcon={
          <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
            <User className="h-5 w-5 text-white" />
          </div>
        }
        title="Ошибка"
        breadcrumbs={[
          { label: 'Главная', href: '/' },
          { label: 'Контакты', href: '/clients' },
          { label: 'Ошибка' },
        ]}
      >
        <div
          data-testid="client-error"
          className="flex flex-col items-center justify-center py-12"
        >
          <AlertCircle className="h-12 w-12 text-red-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Ошибка загрузки клиента
          </h3>
          <p className="text-gray-500 mb-4">
            {error instanceof Error ? error.message : 'Клиент не найден'}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Назад
            </Button>
            <Button onClick={() => refetch()}>Повторить</Button>
          </div>
        </div>
      </PageLayout>
    );
  }

  // Funnel widget: only show stages the operator has not hidden. We treat a
  // missing `is_active` as visible (some payloads omit the flag).
  //
  // `stages` may arrive as a non-array (e.g. a paginated `{ results }` object
  // if the API contract regresses), so we normalize to a guaranteed array
  // before any `.filter`/`.map`/`.sort`/`.find`. A non-array payload yields an
  // empty list → the "Воронка не настроена" branch (soft degradation).
  const stagesArr = Array.isArray(stages) ? stages : [];
  const visibleStages = stagesArr.filter((s) => s.is_active !== false);
  const sortedStages = [...visibleStages].sort((a, b) => a.order - b.order);

  // Contacts widget: same defensive normalization - a non-array payload
  // renders the "Контактов пока нет" empty state instead of crashing on
  // `.map`/`.length`.
  const contactsArr = Array.isArray(contacts) ? contacts : [];

  // TASK-340: real per-funnel positions. When present, the funnel widget
  // renders one SECTION per position (per funnel) and highlights the active
  // stage by STAGE ID - the legacy order/label matching below is only a
  // fallback for clients without positions.
  const funnelPositions: ClientFunnelPosition[] = Array.isArray(
    client.funnel_positions
  )
    ? client.funnel_positions
    : [];

  // Legacy fallback: resolve the client's current stage from `client.stage`.
  // `client.stage` (number id) and the stage-config list (string id) are
  // serialized independently, so we match on the stable business signals
  // first - order, then label - and fall back to a loose id comparison only
  // if neither matches. (Known limitation: order/label may collide across
  // funnels - that is exactly why funnel_positions takes precedence.)
  const activeStage: FunnelStageConfig | undefined = client.stage
    ? visibleStages.find((s) => s.order === client.stage!.order) ??
      visibleStages.find((s) => s.label === client.stage!.label) ??
      visibleStages.find((s) => String(s.id) === String(client.stage!.id))
    : undefined;

  return (
    <>
      <PageLayout
        data-testid="client-detail-page"
        titleIcon={<ClientAvatar avatarUrl={client.avatar_url} name={client.name} size={40} />}
        title={client.name || 'Клиент'}
        subtitle={`Телефон: ${client.phone}`}
        breadcrumbs={[
          { label: 'Главная', href: '/' },
          { label: 'Контакты', href: '/clients' },
          { label: client.name || client.phone },
        ]}
        actions={
          <div className="flex items-center gap-2">
            {/* KAN-833, KAN-877: click-to-call, targets known at once. */}
            <ClientHeaderCall clientId={client.id} phone={client.phone} />
            {/* KAN-960: следующий контакт (звонок или встреча), клиент берётся в работу. */}
            <NextContactAction target={clientNextContactTarget(client)} className="h-10" />
            {/* KAN-924: closed outcome (lost / won) back to work. */}
            <ClientReopenButton
              clientId={client.id}
              outcome={client.outcome}
              junkAt={client.junk_at}
              className="h-10"
            />
            {/* KAN-898: hide the contact from every list (data kept). */}
            <ClientJunkControls
              clientId={client.id}
              junkAt={client.junk_at}
              junkClassName="h-10"
              onJunked={handleBack}
            />
            <Button variant="outline" onClick={handleBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Назад
            </Button>
            {canManage && (
              <Button
                variant="destructive"
                onClick={handleDelete}
                data-testid="delete-button"
              >
                <Trash2 className="h-4 w-4" />
                <span className="hidden sm:inline ml-2">Удалить</span>
              </Button>
            )}
            <Button
              onClick={handleSave}
              disabled={!canSave || updateMutation.isPending}
              data-testid="save-button"
            >
              {updateMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              <span className="hidden sm:inline ml-2">Сохранить</span>
            </Button>
          </div>
        }
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Client Info Card */}
          <div className="lg:col-span-2 space-y-6">
            {/* KAN-815: when and what the bot writes next */}
            <NextBotTouchNote clientId={client.id} touch={client.next_bot_touch} />
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Информация о клиенте
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">
                      <Phone className="h-4 w-4 inline mr-2" />
                      Телефон
                    </Label>
                    <Input
                      id="phone"
                      data-testid="phone-input"
                      value={form.phone || ''}
                      onChange={(e) => handleFormChange('phone', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="name">
                      <User className="h-4 w-4 inline mr-2" />
                      Имя
                    </Label>
                    <Input
                      id="name"
                      data-testid="name-input"
                      value={form.name || ''}
                      onChange={(e) => handleFormChange('name', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">
                      <Mail className="h-4 w-4 inline mr-2" />
                      Email
                    </Label>
                    <Input
                      id="email"
                      data-testid="email-input"
                      type="email"
                      value={form.email || ''}
                      onChange={(e) => handleFormChange('email', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="telegram_username">
                      <MessageCircle className="h-4 w-4 inline mr-2" />
                      Telegram
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="telegram_username"
                        data-testid="telegram-username-input"
                        className="min-w-0"
                        autoComplete="off"
                        spellCheck={false}
                        value={telegramUsername}
                        placeholder="@username"
                        onChange={(e) => setTelegramUsername(e.target.value)}
                      />
                      <TelegramIconLink
                        href={telegramHref}
                        className="shrink-0 text-gray-400 hover:text-blue-600"
                      />
                    </div>
                  </div>
                </div>
                {/* KAN-826: organization of the client (link, change, unlink). */}
                <ClientCompanyField clientId={client.id} company={client.client_company} />
                <div className="space-y-2">
                  <Label htmlFor="notes">Заметки</Label>
                  <Textarea
                    id="notes"
                    data-testid="notes-input"
                    value={form.notes || ''}
                    onChange={(e) => handleFormChange('notes', e.target.value)}
                    rows={4}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Client deals: estimates or orders by cabinet mode (KAN-746). */}
            <ClientDealsCard clientId={client.id} />

            {/* Contacts Card */}
            {canSeeContacts && (
              <Card data-testid="client-contacts-widget">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" />
                    Контакты
                    <Badge variant="outline" className="ml-2">
                      {contactsArr.length}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {contactsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                    </div>
                  ) : contactsArr.length > 0 ? (
                    <div className="space-y-3">
                      {contactsArr.map((contact: FunnelContact) => (
                        <ContactCard key={contact.id} contact={contact} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      Контактов пока нет
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <LeadContactsCard contacts={client.lead_contacts} clientId={client.id} canEdit={canEditCard} />
            {/* KAN-783: what the bot found out (configured lead attributes). */}
            <LeadAttributesCard attributes={client.lead_attributes} />
            {/* KAN-826: own fields of the contact from list imports. */}
            <ClientProfileDataCard data={client.profile_data} />
            {/* Status Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Статус</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Идентификация</span>
                  <Badge variant={client.is_identified ? 'default' : 'outline'}>
                    {client.is_identified ? 'Идентифицирован' : 'Не идентифицирован'}
                  </Badge>
                </div>
                {/* KAN-723: own (test) contact: not in attention, not in the funnel. */}
                <OwnContactSwitch clientId={client.id} isOwn={!!client.is_own} />
                {/* KAN-744: manager of the client, saved on select. */}
                <ClientManagerSelect clientId={client.id} manager={client.manager} />
                {/* KAN-810: lead temperature, saved on select (manual wins over the bot). */}
                <ClientLeadTemperatureSelect
                  clientId={client.id}
                  level={client.lead_temperature}
                  source={client.lead_temperature_source}
                  reason={client.lead_temperature_reason}
                />
                {/* KAN-686: who answers the client right now. */}
                <div
                  className="flex items-center justify-between gap-2"
                  data-testid="client-bot-status"
                >
                  <span className="text-sm text-gray-500">Переписка</span>
                  <span className="text-right text-sm">
                    <BotSilenceStatus
                      clientId={client.id}
                      manualMode={!!client.manual_mode}
                      botSilence={client.bot_silence}
                      testId="client-bot-silence"
                    />
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Telegram</span>
                  <Badge
                    variant={telegramLinked ? 'default' : 'outline'}
                    className={telegramLinked ? 'bg-blue-500' : ''}
                    title={
                      telegramLinked
                        ? 'Привязан: клиент писал нам в Telegram'
                        : 'Не привязан: переписки в Telegram нет'
                    }
                    data-testid="client-telegram-linked"
                  >
                    {telegramLinked ? 'Привязан' : 'Не привязан'}
                  </Badge>
                </div>
                {/* KAN-682: where the client came from (label from the backend). */}
                {client.source_label !== undefined && (
                  <div
                    className="flex items-center justify-between gap-2"
                    data-testid="client-source"
                  >
                    <span className="text-sm text-gray-500">Источник:</span>{' '}
                    <span className="text-sm font-medium text-right">
                      {client.source_label || 'Неизвестно'}
                    </span>
                  </div>
                )}
                {labels.clientDealsCount && (
                  <div className="flex items-center justify-between" data-testid="client-deals-count">
                    <span className="text-sm text-gray-500">{labels.clientDealsCount}</span>
                    <span className="font-medium">{client.estimates_count}</span>
                  </div>
                )}
                {client.followup_at && (
                  <div className="flex items-start justify-between">
                    <span className="text-sm text-gray-500">
                      {client.followup_type_label
                        ? `Следующий шаг: ${client.followup_type_label}`
                        : 'Запланированный созвон'}
                    </span>
                    <div
                      data-testid="client-followup"
                      className="text-right"
                    >
                      <div className="flex items-center justify-end gap-1 text-sm font-medium">
                        <Phone className="h-4 w-4 text-orange-500" />
                        {new Date(client.followup_at).toLocaleString('ru-RU', {
                          day: 'numeric',
                          month: 'long',
                          hour: '2-digit',
                          minute: '2-digit',
                          timeZone: orgTimeZone,
                        })}
                      </div>
                      {client.followup_note && (
                        <div className="text-xs text-gray-500 mt-0.5">
                          {client.followup_note}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <ReferralQueueNotice clientId={client.id} />
            <ClientLinksCard clientId={client.id} />

            {/* Funnel Card */}
            <Card data-testid="client-funnel-widget">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Воронка
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stagesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                  </div>
                ) : funnelPositions.length > 0 ? (
                  // TASK-340: per-funnel sections. One ladder per funnel the
                  // client has a position in; active stage matched by ID.
                  <div className="space-y-5">
                    {funnelPositions.map((position) => {
                      const funnelStages = sortedStages.filter(
                        (s) =>
                          s.funnel != null &&
                          String(s.funnel) === String(position.funnel.id)
                      );
                      return (
                        <div
                          key={position.funnel.codename}
                          data-testid={`funnel-section-${position.funnel.codename}`}
                          className="space-y-2"
                        >
                          {/* KAN-682: with one funnel the card title "Воронка" is enough. */}
                          {funnelPositions.length > 1 && (
                            <div
                              data-testid="funnel-section-name"
                              className="text-sm font-medium text-gray-900"
                            >
                              {position.funnel.name}
                            </div>
                          )}
                          {!position.stage && (
                            <div className="text-center text-xs text-gray-400 mb-1">
                              Стадия не определена
                            </div>
                          )}
                          {funnelStages.map((stage) => {
                            const isActive =
                              position.stage != null &&
                              String(stage.id) === String(position.stage.id);
                            return (
                              <div
                                key={stage.id}
                                data-testid={
                                  isActive ? 'funnel-stage-active' : undefined
                                }
                                className={
                                  isActive
                                    ? 'flex items-center justify-between rounded-md border px-3 py-2 text-sm font-medium bg-blue-600 text-white border-blue-600'
                                    : 'flex items-center justify-between rounded-md border px-3 py-2 text-sm bg-gray-50 text-gray-700 border-gray-200'
                                }
                              >
                                <span>{stage.label}</span>
                                {isActive && (
                                  <span className="text-xs">◀ вы здесь</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                ) : sortedStages.length === 0 ? (
                  <div className="text-center py-6 text-sm text-gray-500">
                    Воронка не настроена
                  </div>
                ) : (
                  <div className="space-y-2">
                    {!client.stage && (
                      <div className="text-center text-xs text-gray-400 mb-1">
                        Стадия не определена
                      </div>
                    )}
                    {sortedStages.map((stage) => {
                      const isActive = activeStage
                        ? stage.id === activeStage.id
                        : false;
                      return (
                        <div
                          key={stage.id}
                          data-testid={isActive ? 'funnel-stage-active' : undefined}
                          className={
                            isActive
                              ? 'flex items-center justify-between rounded-md border px-3 py-2 text-sm font-medium bg-blue-600 text-white border-blue-600'
                              : 'flex items-center justify-between rounded-md border px-3 py-2 text-sm bg-gray-50 text-gray-700 border-gray-200'
                          }
                        >
                          <span>{stage.label}</span>
                          {isActive && <span className="text-xs">◀ вы здесь</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Dates Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Даты
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Создан</span>
                  <span>{formatDate(client.created_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Обновлен</span>
                  <span>{formatDate(client.updated_at)}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </PageLayout>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent data-testid="confirm-delete-dialog">
          <DialogHeader>
            <DialogTitle>Удаление клиента</DialogTitle>
            <DialogDescription>
              Вы уверены, что хотите удалить клиента{' '}
              {client.name || client.phone}? Вместе с клиентом безвозвратно{' '}
              {labels.clientDeleteWhat}. Отменить это действие будет невозможно.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              Отмена
            </Button>
            <Button
              variant="destructive"
              data-testid="confirm-delete-button"
              onClick={handleConfirmDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Удалить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ClientDetailPage;
